import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  BrandWizardSteps,
  BrandWizardStep,
  BRAND_WIZARD_STEP_ORDER,
  BRAND_WIZARD_STEP_LABELS
} from '../../../constants/BrandWizardConstants';
import { PAGE_BRAND_PROFILE } from '../../../constants/UriConstants';
import tapTabLogo from '../../../assets/images/TapTabLargeLogoWhite.png';
import BrandBasicInfoStep, { BrandBasicInfoData } from './BrandBasicInfoStep';
import BrandContactStep, { BrandContactData } from './BrandContactStep';
import { useBrandContext } from '../../../contexts/BrandContext';
import { createBrand } from '../../../api/brand';

const initialBasicInfo: BrandBasicInfoData = {
  brandName: '',
  cuisineType: '',
  streetAddress1: '',
  streetAddress2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  email: ''
};

const initialContact: BrandContactData = {
  firstName: '',
  lastName: '',
  title: '',
  phone: '',
  email: ''
};

interface ProgressBarProps {
  currentStep: BrandWizardStep;
}

const ProgressBar = ({ currentStep }: ProgressBarProps) => {
  const currentIndex = BRAND_WIZARD_STEP_ORDER.indexOf(currentStep);
  const totalSteps = BRAND_WIZARD_STEP_ORDER.length;
  const progressPercent = ((currentIndex + 1) / totalSteps) * 100;

  return (
    <div style={{ width: '100%', maxWidth: '700px', margin: '0 auto', padding: '0 24px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        {BRAND_WIZARD_STEP_ORDER.map((step, index) => (
          <span
            key={step}
            style={{
              fontSize: '0.875rem',
              fontWeight: index === currentIndex ? 700 : 400,
              color: index <= currentIndex ? 'white' : 'rgba(255,255,255,0.5)',
              textAlign: 'center',
              flex: 1
            }}
          >
            {BRAND_WIZARD_STEP_LABELS[step]}
          </span>
        ))}
      </div>
      <div
        style={{ height: '10px', backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '5px', overflow: 'hidden' }}
      >
        <div
          style={{
            height: '100%',
            width: `${progressPercent}%`,
            backgroundColor: 'var(--color-accent)',
            borderRadius: '5px',
            transition: 'width 0.3s ease'
          }}
        />
      </div>
    </div>
  );
};

const BrandWizard = () => {
  const navigate = useNavigate();
  const { brandId } = useParams();
  const { restaurantGroupID, setBrand, refreshBrands } = useBrandContext();
  const [currentStep, setCurrentStep] = useState<BrandWizardStep>(BrandWizardSteps.BASIC_INFO);
  const [basicInfo, setBasicInfo] = useState<BrandBasicInfoData>(initialBasicInfo);
  const [contact, setContact] = useState<BrandContactData>(initialContact);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCancel = useCallback(() => {
    navigate(`/${brandId}/${PAGE_BRAND_PROFILE}`);
  }, [brandId, navigate]);

  const handleSubmit = useCallback(async () => {
    if (!restaurantGroupID || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Create brand with fields the API supports
      const newBrand = await createBrand(restaurantGroupID, {
        name: basicInfo.brandName
        // TODO: Map cuisineType string to cuisineID number
        // cuisineID: lookupCuisineID(basicInfo.cuisineType),
      });

      // TODO: Save contact when contact endpoints are available

      toast.success('Brand created successfully!', {
        position: toast.POSITION.TOP_RIGHT
      });

      // Refresh brands list so dropdown includes the new brand, then select it
      await refreshBrands();
      await setBrand(newBrand.id);
      navigate(`/${newBrand.id}/brand-profile`);
    } catch (error) {
      console.error('Error creating brand:', error);
      toast.error('Failed to create brand. Please try again.', {
        position: toast.POSITION.TOP_RIGHT
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [basicInfo, restaurantGroupID, isSubmitting, setBrand, refreshBrands, navigate]);

  return (
    <div className="restaurant-wizard-container">
      <div className="restaurant-wizard-hero-background" />
      <div className="restaurant-wizard-scroll-body">
        <div className="restaurant-wizard-header">
          <div className="restaurant-wizard-header-badge">
            <img src={tapTabLogo} alt="" className="restaurant-wizard-header-logo" aria-hidden />
            <h1 className="restaurant-wizard-header-title" style={{ marginTop: '12px' }}>
              ADD BRAND
            </h1>
          </div>
        </div>

        <ProgressBar currentStep={currentStep} />

        <div className="restaurant-wizard-content">
          {currentStep === BrandWizardSteps.BASIC_INFO && (
            <BrandBasicInfoStep
              data={basicInfo}
              onChange={setBasicInfo}
              onNext={() => setCurrentStep(BrandWizardSteps.CONTACT)}
              onCancel={handleCancel}
            />
          )}
          {currentStep === BrandWizardSteps.CONTACT && (
            <BrandContactStep
              data={contact}
              onChange={setContact}
              onSubmit={handleSubmit}
              onBack={() => setCurrentStep(BrandWizardSteps.BASIC_INFO)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandWizard;
